// `Stepper` (design-system spec §3.10) — the setup-steps list grammar as a
// shared primitive: 15 px status discs (done = ok-filled check, current =
// accent ring + dot, todo = gray ring), the label, right-aligned 11 px tabular
// meta, and an optional footer primary ("Continue: Plant map →"). The office
// home's setup card and the onboarding wizard shell both speak this grammar;
// one Stepper per screen (§3.10).
//
// Presentational only: the caller supplies state per step and any navigation.
// Rows with `to` render as links (never buttons-styled-as-links — gate §4.3b).

import type { ReactNode } from "react";
import { Link } from "@tanstack/react-router";

export type StepState = "done" | "current" | "todo";

export interface StepperStep {
  key: string;
  label: ReactNode;
  state: StepState;
  /** Right-aligned 11 px tabular meta ("Jul 24", "18 of 21 nodes"). */
  meta?: ReactNode;
  /** Route the row opens; omitted rows are inert. */
  to?: string | undefined;
  "data-testid"?: string;
  /** Extra machine-readable state for walkthroughs (`data-status`). */
  "data-status"?: string;
}

function StepDisc({ state }: { state: StepState }) {
  return (
    <span className="ds-st" aria-hidden="true">
      {state === "done" ? (
        <svg viewBox="0 0 10 10">
          <path d="m2 5.2 2.1 2.1L8 3.2" />
        </svg>
      ) : state === "current" ? (
        <i />
      ) : null}
    </span>
  );
}

export function Stepper({
  steps,
  footer,
  ...rest
}: {
  steps: readonly StepperStep[];
  /** Footer slot — the one "Continue: …" primary, when the caller has one. */
  footer?: ReactNode;
  "data-testid"?: string;
}) {
  return (
    <>
      <ol className="ds-steps" {...rest}>
        {steps.map((step) => {
          const cls =
            step.state === "done"
              ? "ds-step-done"
              : step.state === "current"
                ? "ds-step-cur"
                : "ds-step-todo";
          const body = (
            <>
              <StepDisc state={step.state} />
              <span className="ds-step-lb">{step.label}</span>
              {step.meta !== undefined ? <span className="ds-step-m num">{step.meta}</span> : null}
            </>
          );
          return (
            <li
              key={step.key}
              className={cls}
              data-testid={step["data-testid"]}
              data-status={step["data-status"]}
            >
              {step.to !== undefined ? (
                <Link to={step.to} className="ds-step-link">
                  {body}
                </Link>
              ) : (
                body
              )}
            </li>
          );
        })}
      </ol>
      {footer !== undefined ? <div className="ds-cpad">{footer}</div> : null}
    </>
  );
}
